import staticData from "@/public/data/flight/passengers.json";
import Datepicker from "@/shared/components/common/Datepicker";
import Flex from "@/shared/components/common/Flex";
import Grid from "@/shared/components/common/Grid";
import SelectInput from "@/containers/components/inputs/SelectInput";
import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/shared/components/common/form";
import Input from "@/shared/components/common/input/Input";
import List from "@/shared/components/common/List";
import Separator from "@/shared/components/common/separator";
import type {
    PassengerSchemaType,
    PassengersSchemaType,
} from "@/shared/schemas/flight/passengersSchema";
import {memo} from "react";
import {useFormContext} from "react-hook-form";
import PassengerCardButtons from "./PassengerCardButtons";
import PassengerCardHeader from "./PassengerCardHeader";

interface PassengerCardProps {
    type: "adult" | "child" | "infant";
    isForeign: boolean;
    nationality: NationalityRes;
    recentPassengers: RecentPassenger[];
    changeToggle: (value: boolean) => void;
    passengerIndex: number;
    hasEnoughSeats: boolean;

}

const PassengerCard = memo(function PassengerCard({
                                                      type,
                                                      nationality,
                                                      recentPassengers,
                                                      isForeign,
                                                      changeToggle,
                                                      passengerIndex,
                                                      hasEnoughSeats
                                                  }: PassengerCardProps) {
    const {inputs} = staticData.forms.passengersForm;

    const {control, watch} = useFormContext<PassengersSchemaType>();

    const isInternational = watch(`passengers.${passengerIndex}.isInternational`);
    const passengerNationality = watch(
        `passengers.${passengerIndex}.nationality`,
    );

    // Filter nationality options to exclude IRN when isInternational is true
    const getNationalityOptions = () => {
        const allNationalities = nationality.results.map(
            ({name, code, fa_name}) => ({
                name: fa_name ?? name,
                value: code,
            }),
        );

        return isForeign ? allNationalities : allNationalities.filter((option) => option.value !== "IRN");
    };

    return (
        <>
            <Flex className="flex-col gap-y-3">
                {/* buttons */}
                <PassengerCardButtons
                    changeToggle={changeToggle}
                    type={type}
                    recentPassengers={recentPassengers}
                    passengerIndex={passengerIndex}
                />
                {/* Header */}
                {!isForeign && <PassengerCardHeader passengerIndex={passengerIndex}/>}

                {/* Passengers Inputs */}
                <Grid className="grid-cols-12 !gap-2 !gap-y-6 place-items-end">
                    <List
                        items={
                            inputs[isForeign || isInternational ? "foreign" : "domestic"]
                        }
                        renderItems={({type, label, options, name}) => {
                            const naCodeRestriction =
                                name === "na_code" &&
                                isInternational &&
                                passengerNationality !== "IRN";
                            return (
                                <Grid.GridCol
                                    className="col-span-12 sm:col-span-6 xl:col-span-3 self-end animate-fade-in">
                                    {type === "date" ? (
                                        <Datepicker
                                            control={control}
                                            type={
                                                isForeign || isInternational ? "gregorian" : "jalalli"
                                            }
                                            name={`passengers.${passengerIndex}.${
                                                name as "birth_date" | "passport_expire"
                                            }`}
                                            yearRange={{
                                                end: name === "passport_expire" ? 2040 : null,
                                            }}
                                            label={label}
                                        />
                                    ) : (
                                        <FormField
                                            control={control}
                                            name={`passengers.${passengerIndex}.${
                                                name as keyof PassengerSchemaType
                                            }`}
                                            render={({field}) => (
                                                <FormItem className="relative">
                                                    <FormLabel
                                                        className="text-muted-foreground"
                                                        value={!!field.value}
                                                        variant="dynamic"
                                                    >
                                                        {label}
                                                    </FormLabel>

                                                    {type === "select" ? (
                                                        <SelectInput
                                                            field={field}
                                                            options={
                                                                {
                                                                    gender: options,
                                                                    passport_nationality_code:
                                                                        nationality.results.map(
                                                                            ({name, code}) => ({
                                                                                name: name,
                                                                                value: code,
                                                                            }),
                                                                        ),
                                                                    nationality: getNationalityOptions(),
                                                                }[name] ?? []
                                                            }
                                                        />
                                                    ) : (
                                                        <FormControl>
                                                            <Input
                                                                {...field}
                                                                type={type}
                                                                value={
                                                                    naCodeRestriction
                                                                        ? ""
                                                                        : (field.value as string)
                                                                }
                                                                className="rounded-sm text-foreground disabled:bg-gray-50"
                                                                disabled={naCodeRestriction}
                                                            />
                                                        </FormControl>
                                                    )}

                                                    <FormMessage className="absolute -bottom-[20px]! right-3 text-xs"/>
                                                </FormItem>
                                            )}
                                        />
                                    )}
                                </Grid.GridCol>
                            );
                        }}
                    />
                </Grid>
            </Flex>
            {hasEnoughSeats && (
                <Separator direction="horizontal" className="!bg-slate-300"/>
            )}
        </>
    );
});

export default PassengerCard;
